import { LIST_DATE_FORMAT, LIST_TIME_FORMAT } from '@app/utils/dates/dateFormats';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Modal, Spin, Steps, Tag } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getDoctorVisitById, listDoctorInstructions } from './api';
import DoctorInstructionModal from './DoctorInstructionModal';
import { hasNoVitals, readVisitVitals } from './visitVitals';
import { VisitNotesSection } from '@app/features/notes';

/**
 * One visit, as a doctor sees it.
 *
 * A modal over the visit rather than a drawer beside it, and laid out like the
 * staff visit form — a status stepper, then titled sections of labelled
 * read-only fields — because that IS the product's shape for "a visit" and a
 * doctor reading one should not be looking at a different thing.
 *
 * Every field here is read-only, and nothing says so — every screen a doctor has
 * is read-only, so a notice on this one would be noise. It is done by not
 * rendering a form rather than by disabling one: disabling every control in the
 * staff form is a worse way of saying "you cannot edit this". A doctor cannot
 * move, cancel or complete a visit, and the backend refuses it whatever this
 * component offers.
 *
 * NO MONEY. The visit carries price and discount for staff; the backend strips
 * both for `doc` and nothing here would render them anyway. Same decision as the
 * list's two money flags.
 *
 * What a doctor can WRITE is a note, in the notes section — the same stream the
 * patient's record shows, narrowed to this visit, carrying their own notes and
 * the ones the office addressed to them. `visit_doctor_comments` is gone; the
 * notes table absorbed it. The footer holds the one action that is not a note:
 * sending an instruction, which the office handles.
 */

type Props = {
  visitId: number | null;
  onClose: () => void;
};

const STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'blue',
  IN_PROGRESS: 'processing',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

/** Where the stepper sits per status. CANCELLED never advanced, so it reads as 0. */
const STATUS_STEP: Record<string, number> = {
  SCHEDULED: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
  CANCELLED: 0,
};

const EMPTY = '—';

const Section = ({ title, extra, children }: { title: string; extra?: React.ReactNode; children: React.ReactNode }) => (
  <div className="rounded-xl border border-slate-200">
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60 rounded-t-xl">
      <span className="text-[13px] font-semibold text-slate-700">{title}</span>
      {extra}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Field = ({ label, half, children }: { label: string; half?: boolean; children: React.ReactNode }) => (
  <div className={half ? 'w-[calc(50%-8px)] min-w-[180px]' : 'w-full'}>
    <div className="text-[12px] text-slate-500 mb-1">{label}</div>
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-[7px] text-[13px] text-slate-700 min-h-[36px]">
      {children}
    </div>
  </div>
);

const DoctorVisitModal = ({ visitId, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const [composing, setComposing] = useState<'instruction' | null>(null);

  const open = visitId !== null;

  const visit = useQuery({
    queryKey: ['doctor', 'visit', visitId],
    enabled: open,
    queryFn: () => getDoctorVisitById(visitId as number),
  });

  const instructions = useQuery({
    queryKey: ['doctor', 'visit-instructions', visitId],
    enabled: open,
    queryFn: () => listDoctorInstructions({ visitId: visitId as number }),
  });

  const data = visit.data;
  const status = data?.status ?? 'SCHEDULED';
  const vitals = readVisitVitals(data);
  // The status enum is IN_PROGRESS; its label key is `visits-status-in-progress`.
  // A missing key renders as the key itself, so this mapping is not cosmetic.
  const statusSlug = status.toLowerCase().replace(/_/g, '-');

  const when = (value?: string | null) =>
    value ? dayjs(value).format(`${LIST_DATE_FORMAT} ${LIST_TIME_FORMAT}`) : EMPTY;

  const fullName = (person?: { firstName?: string | null; lastName?: string | null } | null) =>
    person ? `${person.lastName ?? ''} ${person.firstName ?? ''}`.trim() : '';

  const clientId = data?.client?.clientId;
  const patientName = fullName(data?.client) || EMPTY;
  const caregiverNames = data?.caregivers?.map(fullName).filter(Boolean).join(', ');

  const address = data ? [data.street, data.city, data.postalCode].filter(Boolean).join(', ') : '';

  const title = (
    <div className="flex items-center gap-2 pr-8 flex-wrap">
      <span>{t('doctor-visit-modal-title')}</span>
      {data && (
        <span className="text-slate-400 font-normal text-[13px]">
          {when(data.startTime)} · {patientName}
        </span>
      )}
      <Tag color={STATUS_COLORS[status] ?? 'default'} className="!ml-auto !m-0 !text-[11px]">
        {t(`visits-status-${statusSlug}`, status)}
      </Tag>
    </div>
  );

  // No "read only" note: every screen a doctor has is read only, so saying it on
  // one of them is noise rather than information.
  const footer = (
    <div className="flex items-center justify-end gap-2 flex-wrap">
      <Button onClick={onClose}>{t('common-close')}</Button>
      <Button type="primary" onClick={() => setComposing('instruction')}>
        {t('doctor-instruction-submit')}
      </Button>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        width={880}
        style={{ maxWidth: 'calc(100vw - 24px)', top: 16 }}
        title={title}
        footer={footer}
        destroyOnHidden
      >
        <Spin spinning={visit.isLoading}>
          {visit.isError ? (
            <Alert type="error" showIcon message={t('doctor-visit-load-failed')} />
          ) : (
            <div className="flex flex-col gap-3 max-h-[70svh] overflow-y-auto pr-1">
              <Steps
                size="small"
                responsive={false}
                current={STATUS_STEP[status] ?? 0}
                status={status === 'CANCELLED' ? 'error' : undefined}
                items={[
                  { title: t('visits-status-scheduled') },
                  { title: t('visits-status-in-progress') },
                  { title: t('visits-status-completed') },
                ]}
              />

              <Section title={t('doctor-visit-section-details')}>
                <div className="flex flex-wrap gap-4">
                  <Field half label={t('doctor-visit-patient')}>
                    {patientName}
                  </Field>
                  <Field half label={t('doctor-visit-caregivers')}>
                    {caregiverNames || EMPTY}
                  </Field>
                  <Field half label={t('doctor-visit-start')}>
                    {when(data?.startTime)}
                  </Field>
                  <Field half label={t('doctor-visit-end')}>
                    {when(data?.endTime)}
                  </Field>
                  <Field half label={t('doctor-visit-address')}>
                    {address || EMPTY}
                  </Field>
                  <Field half label={t('doctor-visit-completed-at')}>
                    {when(data?.completedAt)}
                  </Field>
                </div>
              </Section>

              {/* No "recorded by the caregiver" caption: a vital on a visit has
                  no other source, so the line said nothing. */}
              <Section title={t('doctor-visit-section-vitals')}>
                {hasNoVitals(vitals) ? (
                  <div className="text-[13px] text-slate-500">{t('doctor-visit-vitals-empty')}</div>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    {vitals.map((reading) => (
                      <Field half key={reading.key} label={t(reading.labelKey)}>
                        {reading.value === null ? (
                          EMPTY
                        ) : (
                          <span className="tabular-nums">
                            {reading.value.toLocaleString(i18n.language)}
                            {reading.unit && <span className="text-slate-400"> {reading.unit}</span>}
                          </span>
                        )}
                      </Field>
                    ))}
                  </div>
                )}
              </Section>

              <Section title={t('doctor-visit-section-services')}>
                {data?.services?.length ? (
                  <div className="flex flex-col divide-y divide-slate-100">
                    {data.services.map((line) => (
                      <div key={line.id} className="flex items-center gap-3 py-2">
                        <span className="text-[13px] text-slate-700 flex-1">{line.service?.name}</span>
                        {status === 'SCHEDULED' ? (
                          <Tag className="!m-0 !text-[11px]">{t('visits-status-scheduled')}</Tag>
                        ) : (
                          <Tag color={line.isCompleted ? 'success' : 'warning'} className="!m-0 !text-[11px]">
                            {t(line.isCompleted ? 'doctor-visit-service-done' : 'doctor-visit-service-pending')}
                          </Tag>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[13px] text-slate-500">{t('doctor-visit-services-empty')}</div>
                )}
              </Section>

              {/* THE VISIT'S NOTES, not a doctor-only comment list.
                  `visit_doctor_comments` is gone — the notes table absorbed it, and
                  a doctor's care-team comment is a MESSAGE note now. So this is the
                  same stream the patient's record shows, narrowed to this visit:
                  the doctor's own notes and the ones the office addressed to them,
                  with whatever else row-level security lets this reader see. The
                  composer inside it is how a doctor writes one; there is no separate
                  comment dialog any more. */}
              {clientId !== undefined && visitId !== null && (
                <Section title={t('notes-visit-panel-title')}>
                  <VisitNotesSection visitId={visitId} clientId={clientId} showHeader={false} maxHeight={280} />
                </Section>
              )}

              <Section title={t('doctor-instructions-heading')}>
                {instructions.data?.data?.length ? (
                  <div className="flex flex-col gap-1.5">
                    {instructions.data.data.map((instruction) => (
                      <div
                        key={instruction.instructionId}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 flex-wrap"
                      >
                        <span className="text-[13px] text-slate-700">{instruction.title}</span>
                        <Tag className="!m-0 !text-[11px]">
                          {t(`doctor-instruction-priority-${instruction.priority.toLowerCase()}`)}
                        </Tag>
                        <Tag
                          className="!m-0 !ml-auto !text-[11px]"
                          color={
                            instruction.status === 'HANDLED'
                              ? 'green'
                              : instruction.status === 'NOT_HANDLED'
                                ? 'red'
                                : 'orange'
                          }
                        >
                          {t(`doctor-instruction-status-${instruction.status.toLowerCase()}`)}
                        </Tag>
                        {instruction.details && (
                          <div className="w-full text-[12px] text-slate-500">{instruction.details}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <Empty
                    className="!my-2"
                    description={t('doctor-instructions-empty')}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Section>
            </div>
          )}
        </Spin>
      </Modal>

      <DoctorInstructionModal open={composing === 'instruction'} visitId={visitId} onClose={() => setComposing(null)} />
    </>
  );
};

export default DoctorVisitModal;
